import React, { useState, useEffect } from 'react';
import { Student, Parent } from '../../types';
import { XMarkIcon, PlusIcon, TrashIcon, PhoneIcon, ClipboardDocumentListIcon, ArrowPathIcon, EyeIcon, EyeSlashIcon } from './Icons';
import { EDU_CONFIG } from '../../config';

import Spinner from './Spinner';

interface StudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (studentData: Omit<Student, 'id'>) => void;
  studentToEdit?: Student | null;
  isSaving?: boolean;
  /** Khi sửa HS đã có trong lớp: hiển thị mã đăng nhập */
  currentClassId?: string | null;
  currentClassName?: string;
  onRegenerateLoginCode?: () => void;
  onCopyLoginInfo?: () => void;
  isRegeneratingCode?: boolean;
  copiedLoginId?: string;
}

const StudentModal: React.FC<StudentModalProps> = ({ isOpen, onClose, onSave, studentToEdit, isSaving, currentClassId, currentClassName, onRegenerateLoginCode, onCopyLoginInfo, isRegeneratingCode, copiedLoginId }) => {
  const [fullName, setFullName] = useState('');
  const [nickname, setNickname] = useState('');
  const [studentId, setStudentId] = useState('');
  const [dob, setDob] = useState('');
  const [email, setEmail] = useState('');
  const [parents, setParents] = useState<Parent[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isCodeVisible, setIsCodeVisible] = useState(false);

  useEffect(() => {
    if (studentToEdit) {
      const s = studentToEdit as any;
      const fn = studentToEdit.name || s.name || '';
      const nn = (studentToEdit.realName || s.real_name || s.realName || '') as string;
      setFullName(fn);
      setNickname(nn);
      setStudentId(studentToEdit.studentId || s.student_id_code || s.studentIdCode || '');
      setDob(studentToEdit.dob || s.date_of_birth || s.dateOfBirth || '');
      setEmail(studentToEdit.email || s.email || '');
      setParents(studentToEdit.parents || s.parents || []);
    } else {
      setFullName('');
      setNickname('');
      setStudentId('');
      setDob('');
      setEmail('');
      setParents([]);
    }
    setErrors({});
  }, [studentToEdit, isOpen]);

  const validate = (): boolean => {
    const err: Record<string, string> = {};
    const nameTrim = fullName.trim();
    if (!nameTrim) err.fullName = 'Vui lòng nhập tên đầy đủ';
    else if (nameTrim.length < 2) err.fullName = 'Tên phải có ít nhất 2 ký tự';
    if (email.trim()) {
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(email.trim())) err.email = 'Email không hợp lệ';
    }
    if (dob) {
      const d = new Date(dob);
      if (isNaN(d.getTime())) err.dob = 'Ngày sinh không hợp lệ';
      else if (d > new Date()) err.dob = 'Ngày sinh không thể ở tương lai';
    }
    parents.forEach((p, i) => {
      const hasName = (p.name || '').trim();
      const hasPhone = (p.phoneNumber || '').replace(/\s/g, '');
      if (hasPhone && !hasName) err[`parent_${i}_name`] = 'Vui lòng nhập tên phụ huynh';
      else if (hasPhone && !/^0\d{9,10}$/.test(hasPhone)) err[`parent_${i}_phone`] = 'SĐT phải từ 10-11 số và bắt đầu bằng 0';
    });
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const nameTrim = fullName.trim();
    if (nameTrim) {
      onSave({
        name: nameTrim,
        realName: nickname.trim() || undefined,
        studentId: studentId.trim() || undefined,
        dob: dob || undefined,
        email: email.trim() || undefined,
        parents: parents.map(p => ({
          ...p,
          name: (p.name || '').trim(),
          phoneNumber: (p.phoneNumber || '').replace(/\s/g, ''),
          relationship: p.relationship || 'Bố'
        })).filter(p => p.name || p.phoneNumber),
      });
    }
  };

  const handleAddParent = () => {
    if (parents.length >= EDU_CONFIG.MAX_PARENTS_PER_STUDENT) return;
    const newParent: Parent = {
        id: `p-${Date.now()}`,
        name: '',
        phoneNumber: '',
        relationship: 'Bố'
    };
    setParents([...parents, newParent]);
  };

  const handleUpdateParent = (id: string, field: keyof Parent, value: string) => {
    setParents(parents.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const handleRemoveParent = (id: string) => {
    setParents(parents.filter(p => p.id !== id));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto py-8" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 flex flex-col max-h-full" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex justify-between items-center flex-shrink-0 bg-teal-600 text-white rounded-t-2xl">
          <h3 className="text-xl font-bold uppercase tracking-widest">{studentToEdit ? 'Cập nhật Học sinh' : 'Thêm Học sinh mới'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors"><XMarkIcon className="h-6 w-6 text-white" /></button>
        </div>
        
        <div className="p-6 space-y-6 overflow-y-auto flex-grow">
          {/* 1. Thông tin cơ bản - Tên đầy đủ bắt buộc, biệt danh tùy chọn */}
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Tên đầy đủ <span className="text-rose-500">*</span></label>
              <input type="text" value={fullName} onChange={e => { setFullName(e.target.value); setErrors(prev => ({ ...prev, fullName: '' })); }} className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl focus:border-teal-500 focus:bg-white transition-all font-bold text-sm ${errors.fullName ? 'border-rose-300 bg-rose-50/30' : 'border-slate-100'}`} placeholder="Ví dụ: Nguyễn Văn An" />
              {errors.fullName && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.fullName}</p>}
              {!errors.fullName && <p className="mt-1 text-[10px] text-slate-400">Dùng xuyên suốt trong danh sách và báo cáo</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Biệt danh / Tên gọi</label>
              <input type="text" value={nickname} onChange={e => setNickname(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-teal-500 focus:bg-white transition-all text-sm font-medium" placeholder="Ví dụ: Miu, An (để trống nếu không có)" />
              <p className="mt-1 text-[10px] text-slate-400">Tùy chọn, hiển thị kèm khi có</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Mã số học sinh</label>
                <input type="text" value={studentId} onChange={e => setStudentId(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-teal-500 focus:bg-white transition-all text-sm font-medium" placeholder="HS001 (để trống = tự sinh)" />
                <p className="mt-1 text-[10px] text-slate-400">Để trống để hệ thống tự sinh mã. Nếu trùng, tự tạo mã mới (vd: HS001-2)</p>
              </div>
<div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Ngày sinh</label>
                <input type="date" value={dob} onChange={e => { setDob(e.target.value); setErrors(prev => ({ ...prev, dob: '' })); }} className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl focus:border-teal-500 focus:bg-white transition-all text-sm font-medium ${errors.dob ? 'border-rose-300 bg-rose-50/30' : 'border-slate-100'}`} />
                {errors.dob && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.dob}</p>}
                {!errors.dob && <p className="mt-1 text-[10px] text-slate-400">Dùng cho đăng nhập mã (4 số DDMM)</p>}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Email học sinh</label>
              <input type="email" value={email} onChange={e => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: '' })); }} className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl focus:border-teal-500 focus:bg-white transition-all text-sm font-medium ${errors.email ? 'border-rose-300 bg-rose-50/30' : 'border-slate-100'}`} placeholder="student@school.edu.vn" />
              {errors.email && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.email}</p>}
            </div>
          </div>

          {/* 2. Mã đăng nhập (chỉ khi sửa HS đã có trong lớp) */}
          {studentToEdit && currentClassId && (
            <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl space-y-3">
              <h4 className="text-xs font-black text-amber-800 uppercase tracking-widest">Mã đăng nhập cho học sinh</h4>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm font-bold text-amber-700 bg-white px-3 py-1.5 rounded-lg border border-amber-200 min-w-[100px] text-center">
                  {(studentToEdit.loginCode || (studentToEdit as any).login_code) 
                    ? (isCodeVisible ? (studentToEdit.loginCode || (studentToEdit as any).login_code).toString().toUpperCase() : '********')
                    : 'CHƯA CÓ MÃ'}
                </span>
                
                {(studentToEdit.loginCode || (studentToEdit as any).login_code) ? (
                  <>
                    <button type="button" onClick={() => setIsCodeVisible(!isCodeVisible)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-amber-700 bg-white border border-amber-200 rounded-lg hover:bg-amber-50 transition-colors">
                      {isCodeVisible ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
                      {isCodeVisible ? 'Ẩn mã' : 'Xem mã'}
                    </button>
                    {onRegenerateLoginCode && (
                      <button type="button" onClick={onRegenerateLoginCode} disabled={isRegeneratingCode} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-amber-700 bg-white border border-amber-200 rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50">
                        {isRegeneratingCode ? <Spinner className="h-3.5 w-3.5" /> : <ArrowPathIcon className="h-3.5 w-3.5" />}
                        Tạo mã mới
                      </button>
                    )}
                    {onCopyLoginInfo && (
                      <button type="button" onClick={onCopyLoginInfo} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-amber-700 bg-white border border-amber-200 rounded-lg hover:bg-amber-50 transition-colors">
                        <ClipboardDocumentListIcon className="h-3.5 w-3.5" />
                        {copiedLoginId === String(studentToEdit.id) ? 'Đã chép!' : 'Sao chép thông tin đăng nhập'}
                      </button>
                    )}
                  </>
                ) : (
                  onRegenerateLoginCode && (
                    <button type="button" onClick={onRegenerateLoginCode} disabled={isRegeneratingCode} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-amber-700 bg-white border border-amber-200 rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50">
                      {isRegeneratingCode ? <Spinner className="h-3.5 w-3.5" /> : <PlusIcon className="h-3.5 w-3.5" />}
                      Cấp mã đăng nhập
                    </button>
                  )
                )}
              </div>
              <p className="text-[10px] text-amber-600">Dùng để học sinh chủ động đăng nhập làm bài/nhiệm vụ. Mã: 8 ký tự + ngày sinh (4 số DDMM).</p>
            </div>
          )}

          {/* 3. Phụ huynh */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Phụ huynh kèm theo ({parents.length}/{EDU_CONFIG.MAX_PARENTS_PER_STUDENT})</h4>
                {parents.length < EDU_CONFIG.MAX_PARENTS_PER_STUDENT && (
                    <button 
                        onClick={handleAddParent}
                        className="flex items-center gap-1 text-xs font-bold text-teal-600 hover:text-teal-700 bg-teal-50 px-2 py-1 rounded-lg transition-colors"
                    >
                        <PlusIcon className="h-3.5 w-3.5" /> Thêm phụ huynh
                    </button>
                )}
            </div>

            {parents.length === 0 ? (
                <div className="text-center py-6 bg-gray-50 border-2 border-dashed rounded-2xl">
                    <p className="text-xs text-gray-500 italic">Chưa có thông tin phụ huynh. Thêm để theo dõi tình hình học tập của con.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {parents.map((parent, idx) => (
                        <div key={parent.id} className="p-4 bg-white border-2 border-gray-100 rounded-2xl relative group">
                            <button 
                                onClick={() => handleRemoveParent(parent.id)}
                                className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                            >
                                <TrashIcon className="h-4 w-4" />
                            </button>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 mb-1">Họ tên & Quan hệ</label>
                                    <div className="flex gap-2">
                                        <input 
                                            type="text" 
                                            value={parent.name} 
                                            onChange={e => { handleUpdateParent(parent.id, 'name', e.target.value); setErrors(prev => ({ ...prev, [`parent_${idx}_name`]: '', [`parent_${idx}_phone`]: '' })); }}
                                            placeholder="Tên phụ huynh"
                                            className="flex-grow text-xs px-3 py-1.5 border rounded-lg focus:ring-1 focus:ring-teal-500"
                                        />
                                        <select 
                                            value={parent.relationship}
                                            onChange={e => handleUpdateParent(parent.id, 'relationship', e.target.value)}
                                            className="w-20 text-xs px-2 py-1.5 border rounded-lg bg-gray-50"
                                        >
                                            <option value="Bố">Bố</option>
                                            <option value="Mẹ">Mẹ</option>
                                            <option value="Ông">Ông</option>
                                            <option value="Bà">Bà</option>
                                            <option value="Khác">Khác</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 mb-1">Số điện thoại liên hệ</label>
                                    <div className="relative">
                                        <PhoneIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
                                        <input 
                                            type="tel" 
                                            value={parent.phoneNumber} 
                                            onChange={e => { handleUpdateParent(parent.id, 'phoneNumber', e.target.value); setErrors(prev => ({ ...prev, [`parent_${idx}_phone`]: '', [`parent_${idx}_name`]: '' })); }}
                                            placeholder="09xx xxx xxx (10-11 số)"
                                            className={`w-full text-xs pl-8 pr-3 py-1.5 border rounded-lg focus:ring-1 focus:ring-teal-500 ${errors[`parent_${idx}_phone`] || errors[`parent_${idx}_name`] ? 'border-rose-300' : ''}`}
                                        />
                                        {(errors[`parent_${idx}_phone`] || errors[`parent_${idx}_name`]) && <p className="mt-0.5 text-[10px] text-rose-600">{errors[`parent_${idx}_phone`] || errors[`parent_${idx}_name`]}</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
          </div>
        </div>

        <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 rounded-b-2xl border-t flex-shrink-0">
          <button onClick={onClose} disabled={isSaving} className="px-6 py-2 bg-white border border-slate-200 text-slate-500 font-bold text-sm rounded-xl hover:bg-slate-100 transition-all">Hủy</button>
          <button onClick={handleSave} className="flex items-center gap-2 px-10 py-2 text-sm font-bold text-white bg-teal-600 rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-100 active:scale-95 disabled:opacity-50 transition-all" disabled={!fullName.trim() || isSaving}>
            {isSaving && <Spinner className="h-4 w-4" />}
            <span>Lưu học sinh</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentModal;
